// src/pages/Tasks/TaskList.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ListChecks, CheckCircle2, AlertTriangle, Plus, ArrowDownUp, ChevronLeft, ChevronRight, Sparkles, Trash2, Layers, User as UserIcon, Users } from 'lucide-react';
import { tasksApi, Task, TaskFilters } from '../../api/tasks';
import TaskFiltersComponent from '../../components/TaskFilters';
import TaskCard from '../../components/TaskCard';
import TaskCreate from './TaskCreate';
import { BoardView } from '../../components/BoardView';
import { TaskStats } from '../../components/TaskStats';

type ScopeTab = 'all' | 'mine' | 'team';

function TaskList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const assignedTo = searchParams.get("assigned_to");
  const includeClosedFromUrl = searchParams.get("include_closed") === "true";
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const initialScope: ScopeTab =
    location.pathname.includes('/my-tasks') ? 'mine' :
    location.pathname.includes('/team-tasks') ? 'team' : 'all';
  const [scope, setScope] = useState<ScopeTab>(initialScope);

  useEffect(() => {
    setScope(
      location.pathname.includes('/my-tasks') ? 'mine' :
      location.pathname.includes('/team-tasks') ? 'team' : 'all'
    );
  }, [location.pathname]);
  const [filters, setFilters] = useState<TaskFilters>({
    page: 1,
    page_size: 20,
    sort_by: 'due_date',
    sort_dir: 'asc',
    include_closed: true,
    ...(assignedTo && { assigned_to: assignedTo }),
  });

  useEffect(() => {
  const status = searchParams.get('status');
  const overdue = searchParams.get('overdue');
  const dueToday = searchParams.get('due_today');
  const unassigned = searchParams.get('unassigned');
  const assignedTo = searchParams.get('assigned_to');
  const includeClosed = searchParams.get('include_closed') === 'true';

  const updates: Partial<TaskFilters> = {};

  if (status) updates.status = status;
  if (overdue === 'true') updates.overdue = true;
  if (unassigned === 'true') updates.assigned_to = '';
  if (assignedTo) updates.assigned_to = assignedTo; // 👈 ADD THIS
  if (includeClosed) updates.include_closed = true; // 👈 ADD THIS

  if (dueToday === 'true') {
    const today = new Date();
    today.setHours(23, 59, 59);
    updates.due_before = today.toISOString();
  }

  if (Object.keys(updates).length > 0) {
    setFilters((prev) => ({ ...prev, ...updates, page: 1 }));
  }
}, [searchParams]);

  const loadTasks = useCallback(async () => {
    setLoading(true);

    try {
      const includeClosed = filters.include_closed ?? true;
      let items: Task[] = [];
      let totalCount = 0;

      if (scope === 'mine') {
        const r = await tasksApi.getMyTasks(includeClosed);
        items = r.data.items || [];
        totalCount = items.length;
      } else if (scope === 'team') {
        const r = await tasksApi.getTeamTasks(includeClosed);
        items = r.data.items || [];
        totalCount = items.length;
      } else {
        // ALL — uses paginated /tasks list with filters
        const finalFilters = {
          ...filters,
          include_closed: includeClosed,
          ...(assignedTo && { assigned_to: assignedTo }),
        };
        const response = await tasksApi.list(finalFilters);
        items = response.data.items || [];
        totalCount = response.data.total || items.length;
      }

      // Apply client-side filters for mine/team scopes (the dedicated endpoints
      // don't accept the same filter params as /tasks)
      if (scope !== 'all') {
        if (filters.status) items = items.filter(t => t.status === filters.status);
        if (filters.priority) items = items.filter(t => t.priority === filters.priority);
        if (filters.search) {
          const q = filters.search.toLowerCase();
          items = items.filter(t =>
            t.title.toLowerCase().includes(q) ||
            (t.description || '').toLowerCase().includes(q)
          );
        }
        if (filters.assigned_to) items = items.filter(t => t.primary_assignee_id === filters.assigned_to);
        if (filters.overdue) items = items.filter(t => t.is_overdue && !['DONE', 'CANCELLED'].includes(t.status));
        totalCount = items.length;
      }

      setTasks(items);
      setTotal(totalCount);
    } catch (err) {
      console.error("Error loading tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, assignedTo, scope]);

const isFirstRender = useRef(true);

useEffect(() => {
  if (isFirstRender.current) {
    isFirstRender.current = false;
    if (assignedTo) return;
  }

  loadTasks();
}, [filters, assignedTo, scope]);

const isFromNavigation = !!assignedTo;

  const handleFilterChange = (newFilters: Partial<TaskFilters>) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
    window.scrollTo(0, 0);
  };

  const handleStatusClick = (status: string) => {
    if (status === 'ALL') {
      setFilters({ ...filters, status: undefined, overdue: undefined });
    } else if (status === 'OVERDUE') {
      setFilters({ ...filters, status: undefined, overdue: true });
    } else {
      setFilters({ ...filters, status, overdue: undefined });
    }
  };

  const totalPages = Math.ceil(total / filters.page_size!);
  const openTasksCount = tasks.filter(t => !['DONE', 'CANCELLED'].includes(t.status)).length;
  const overdueCount = tasks.filter(t => t.is_overdue && !['DONE', 'CANCELLED'].includes(t.status)).length;

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>tasks</em>…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-md text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          <AlertTriangle className="w-9 h-9 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
          <p className="gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>{error}</p>
          <button onClick={loadTasks} className="gt-btn is-danger mt-4 mx-auto">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="gt-shell min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
          {/* Header */}
          <div className="gt-page-hero flex-col md:flex-row gap-4">
            <div>
              <div className="gt-eyebrow-row">
                <span className="gt-leaf" />
                <span className="gt-eyebrow">ii. Overview</span>
              </div>
              <h1 className="gt-page-title">Task <em>Management</em></h1>
              <p className="gt-page-sub" style={{ marginTop: 6 }}>Manage and track all your <em className="gt-serif-italic">organizational</em> tasks.</p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setIsCreateModalOpen(true)} className="gt-btn is-primary is-lg">
                <Plus className="w-4 h-4" />
                Create New Task
              </button>
              <button
                onClick={() => navigate('/tasks/recently-deleted')}
                className="gt-btn is-danger"
              >
                <Trash2 className="w-4 h-4" />
                Recently Deleted
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {tasks.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="gt-stat">
                <div className="gt-stat-label"><ListChecks className="w-3.5 h-3.5" /> Total Tasks</div>
                <p className="gt-stat-value">{total}</p>
              </div>
              <div className="gt-stat">
                <div className="gt-stat-label"><CheckCircle2 className="w-3.5 h-3.5" /> Open Tasks</div>
                <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>{openTasksCount}</p>
              </div>
              <div className="gt-stat" style={{ borderColor: overdueCount > 0 ? 'rgba(220,38,38,0.18)' : undefined }}>
                <div className="gt-stat-label"><AlertTriangle className="w-3.5 h-3.5" /> Overdue</div>
                <p className="gt-stat-value" style={{ color: overdueCount > 0 ? '#991b1b' : undefined }}>{overdueCount}</p>
              </div>
            </div>
          )}

          {/* Scope Tabs — All / My / Team */}
          <div style={{ borderBottom: '1px solid var(--gt-border)' }}>
            <nav className="flex gap-1 flex-wrap">
              {[
                { id: 'all'  as ScopeTab, label: 'All Tasks',  icon: <Layers className="w-4 h-4" /> },
                { id: 'mine' as ScopeTab, label: 'My Tasks',   icon: <UserIcon className="w-4 h-4" /> },
                { id: 'team' as ScopeTab, label: 'Team Tasks', icon: <Users className="w-4 h-4" /> },
              ].map(tab => {
                const isActive = scope === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setScope(tab.id); setFilters(f => ({ ...f, page: 1 })); }}
                    className="transition-all"
                    style={{
                      padding: '10px 18px',
                      fontFamily: 'var(--gt-font-display)',
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      border: 'none',
                      borderBottom: isActive ? '2px solid var(--gt-primary)' : '2px solid transparent',
                      background: isActive ? 'var(--gt-surface-0)' : 'transparent',
                      color: isActive ? 'var(--gt-primary-deep)' : 'var(--gt-text-tertiary)',
                      boxShadow: isActive ? 'var(--gt-elev-1)' : 'none',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: -1,
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Task Stats */}
          <TaskStats tasks={tasks} onStatusClick={handleStatusClick} />

          {/* Filters */}
          <TaskFiltersComponent filters={filters} onFilterChange={handleFilterChange} />

          {/* Tasks Display */}
          {tasks.length === 0 ? (
            <div className="gt-card p-12 text-center">
              <div className="gt-empty">
                <div className="gt-empty-leaf"><Sparkles className="w-6 h-6" /></div>
                <div className="gt-empty-title">No tasks yet</div>
                <div className="gt-page-sub">A clean slate. <em className="gt-serif-italic">Begin</em> with the first one.</div>
                <button onClick={() => setIsCreateModalOpen(true)} className="gt-btn is-primary mt-5 mx-auto">
                  <Plus className="w-4 h-4" />
                  Create your first task
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <p className="gt-page-sub gt-numerals">
                  Showing <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{tasks.length}</span> of{" "}
                  <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{total}</span> tasks
                </p>
                <div className="flex items-center gap-2 gt-page-sub" style={{ background: 'var(--gt-surface-2)', padding: '6px 12px', borderRadius: 999 }}>
                  <ArrowDownUp className="w-3.5 h-3.5" />
                  <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Sorted by due date</span>
                </div>
              </div>

              {viewMode === 'list' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {tasks.map((task) => (
                    <TaskCard key={task.id} task={task} onRefresh={loadTasks} />
                  ))}
                </div>
              ) : (
                <BoardView tasks={tasks} onRefresh={loadTasks} />
              )}

              {viewMode === 'list' && totalPages > 1 && (
                <>
                  <div className="mt-8 flex justify-center items-center gap-2">
                    <button
                      onClick={() => handlePageChange(filters.page! - 1)}
                      disabled={filters.page === 1}
                      className="gt-btn is-ghost is-sm"
                    >
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>

                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) pageNum = i + 1;
                        else if (filters.page! <= 3) pageNum = i + 1;
                        else if (filters.page! >= totalPages - 2) pageNum = totalPages - 4 + i;
                        else pageNum = filters.page! - 2 + i;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={filters.page === pageNum ? 'gt-btn is-primary is-sm' : 'gt-btn is-ghost is-sm'}
                            style={{ width: 36, height: 36, justifyContent: 'center', padding: 0 }}
                          >
                            <span className="gt-numerals">{pageNum}</span>
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(filters.page! + 1)}
                      disabled={filters.page === totalPages}
                      className="gt-btn is-ghost is-sm"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="text-center gt-page-sub gt-numerals md:hidden">
                    Page {filters.page} of {totalPages}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <TaskCreate
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => loadTasks()}
      />
    </>
  );
}

export default TaskList;